import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, message, Typography, Result, Alert, Spin } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined } from '@ant-design/icons';

import PageTitle from '../Layouts/PageTitle';
import authService from '../../services/auth.service';
import './CompleteAccount.scss';

const { Title, Text } = Typography;

type AccountSetupFormValues = {
  username?: string;
  email?: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
};

const CompleteAccount: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [completed, setCompleted] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get token from URL query params
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('token');

    if (inviteToken) {
      setToken(inviteToken);
      verifyToken(inviteToken);
    } else {
      setVerificationAttempted(true);
      message.error('No invitation token provided');
    }
  }, [location]);

  const verifyToken = async (inviteToken: string) => {
    try {
      setLoading(true);
      const response = await authService.verifyInvitationToken(inviteToken);

      if (response.data.valid) {
        setTokenValid(true);
        setUserEmail(response.data.email);
        setUsername(response.data.username);
        setFirstName(response.data.firstName || '');
        setLastName(response.data.lastName || '');
        // Pre-fill the form with email, username, and names
        form.setFieldsValue({
          email: response.data.email,
          username: response.data.username,
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
        });
      } else {
        message.error('Invalid or expired invitation token');
      }
    } catch (error) {
      console.error('Failed to verify token:', error);
      message.error('Failed to verify invitation token');
    } finally {
      setLoading(false);
      setVerificationAttempted(true);
    }
  };

  const completeAccountSetup = async (values: AccountSetupFormValues) => {
    try {
      setLoading(true);

      // Update password
      await authService.completeAccountSetup(token, values.password);

      // Update user profile in one step
      await authService.updateUserProfile(token, {
        firstName: values.firstName,
        lastName: values.lastName,
      });

      message.success('Account setup completed successfully');
      setCompleted(true);

      // Automatically redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/sign-in');
      }, 3000);
    } catch (error) {
      console.error('Failed to complete account setup:', error);
      message.error('Failed to complete account setup');
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => (
    <Form
      form={form}
      name="account_setup"
      onFinish={completeAccountSetup}
      layout="vertical"
      requiredMark={false}
    >
      <Alert
        title="Account Information"
        description={
          <div>
            <p>
              Please use the following username <br />
              when logging in:
            </p>
            <Text strong style={{ fontSize: '16px' }}>
              {username}
            </Text>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form.Item name="username" label="Username" style={{ display: 'none' }}>
        <Input prefix={<UserOutlined />} disabled />
      </Form.Item>

      <Form.Item name="email" label="Email">
        <Input prefix={<MailOutlined />} disabled />
      </Form.Item>

      <Form.Item
        name="password"
        label="New Password"
        rules={[
          { required: true, message: 'Please enter your new password' },
          { min: 8, message: 'Password must be at least 8 characters' },
          {
            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
            message: 'Password must contain uppercase, lowercase, number and special character',
          },
        ]}
      >
        <Input.Password prefix={<LockOutlined />} />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label="Confirm Password"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Please confirm your password' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('The two passwords do not match'));
            },
          }),
        ]}
      >
        <Input.Password prefix={<LockOutlined />} />
      </Form.Item>

      <Form.Item
        name="firstName"
        label="First Name"
        rules={[{ required: true, message: 'Please enter your first name' }]}
      >
        <Input prefix={<UserOutlined />} />
      </Form.Item>

      <Form.Item
        name="lastName"
        label="Last Name"
        rules={[{ required: true, message: 'Please enter your last name' }]}
      >
        <Input prefix={<UserOutlined />} />
      </Form.Item>

      <Form.Item>
        <Button size={'small'} type="primary" htmlType="submit" loading={loading} block>
          Complete Account Setup
        </Button>
      </Form.Item>
    </Form>
  );

  const renderSuccess = () => (
    <Result
      status="success"
      title="Account Setup Completed!"
      subTitle="Your account has been successfully set up. You will be redirected to the login page in a few seconds."
      extra={[
        <Button size={'small'} type="primary" key="login" onClick={() => navigate('/sign-in')}>
          Go to Login
        </Button>,
      ]}
    />
  );

  if (!verificationAttempted) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f0f2f5',
        }}
      >
        <Card style={{ width: 450, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 20 }}>Verifying your invitation...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!tokenValid && verificationAttempted && !completed) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f0f2f5',
        }}
      >
        <Card style={{ maxWidth: 450, width: '100%', margin: '10px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <Result
            status="warning"
            title="Invalid or Expired Invitation"
            subTitle="The invitation link you've used is invalid or has expired. Please contact your administrator for a new invitation."
            extra={[
              <Button
                size={'small'}
                type="primary"
                key="login"
                onClick={() => navigate('/sign-in')}
              >
                Go to Login
              </Button>,
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <PageTitle title="Complete Account">
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f0f2f5',
        }}
      >
        <Card style={{ maxWidth: 450, width: '100%', margin: '10px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          {completed ? (
            renderSuccess()
          ) : (
            <>
              <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
                Complete Your Account Setup
              </Title>
              {renderForm()}
            </>
          )}
        </Card>
      </div>
    </PageTitle>
  );
};

export default CompleteAccount;
